import React, { useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Globe, Check } from 'lucide-react';

interface ColumnInfo {
  name: string;
  type: string;
}

interface WebsiteColumnResult {
  column: string;
  uniqueCount: number;
}

interface WebsiteColumnSelectionProps {
  columns: ColumnInfo[];
  results: WebsiteColumnResult[];
  onAnalyze: (selectedColumns: string[], searchForMissing: boolean) => void;
  disabled?: boolean;
}

export const WebsiteColumnSelection: React.FC<WebsiteColumnSelectionProps> = ({ 
  columns, 
  results, 
  onAnalyze, 
  disabled 
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [searchForMissing, setSearchForMissing] = useState<boolean>(false);

  const handleColumnToggle = (columnName: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnName) 
        ? prev.filter(col => col !== columnName)
        : [...prev, columnName]
    );
  };

  const handleAnalyze = () => {
    onAnalyze(selectedColumns, searchForMissing);
  };

  if (disabled && results.length > 0) {
    return (
      <div className="border border-slate-600 rounded-lg p-4 bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <span className="font-medium text-white">Website column analysis completed</span>
          <Badge variant="outline" className="text-green-400 border-green-400">
            <Check className="w-3 h-3 mr-1" />
            Analyzed
          </Badge>
        </div>
        <div className="space-y-2">
          {results.map(result => (
            <div key={result.column} className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">{result.column}</span>
              <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                {result.uniqueCount.toLocaleString()} unique websites
              </Badge>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-slate-400">
        Select column(s) with website addresses. We'll use these to gather company information.
      </p>

      <div className="space-y-3">
        {columns.filter(col => col.type === 'string').map((column) => (
          <div key={column.name} className="flex items-center space-x-3 p-3 border border-slate-600 rounded-lg bg-slate-800">
            <Checkbox
              id={column.name}
              checked={selectedColumns.includes(column.name)}
              onCheckedChange={() => handleColumnToggle(column.name)}
              disabled={disabled}
              className="border-slate-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600 data-[state=checked]:text-white"
            />
            <label htmlFor={column.name} className="flex items-center gap-2 cursor-pointer flex-1 text-white">
              <Globe className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{column.name}</span>
            </label>
            {results.find(r => r.column === column.name) && (
              <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                {results.find(r => r.column === column.name)?.uniqueCount.toLocaleString()} unique
              </Badge>
            )}
          </div>
        ))}
      </div>

      {!disabled && (
        <div className="border border-slate-600 rounded-lg bg-slate-800 p-4">
          <div className="flex items-center space-x-3 mb-2">
            <Checkbox
              id="search-missing"
              checked={searchForMissing}
              onCheckedChange={(checked: boolean) => setSearchForMissing(checked === true)}
              className="border-slate-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600 data-[state=checked]:text-white"
            />
            <label htmlFor="search-missing" className="text-white cursor-pointer font-medium">
              Search for missing website data
            </label>
          </div>
          <p className="text-sm text-slate-400 ml-7">
            AlphaLoop can search Google to fill in missing web site data if your web site data is sparse or missing.
          </p>
        </div>
      )}

      {selectedColumns.length > 0 && !disabled && (
        <Button 
          onClick={handleAnalyze} 
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
        >
          Analyze Selected Columns ({selectedColumns.length})
        </Button>
      )}

      {selectedColumns.length === 0 && !disabled && (
        <p className="text-sm text-slate-500 text-center py-4">
          Please select at least one column to analyze
        </p>
      )}
    </div>
  );
}; 